import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Internal Helper Components --- //

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ShaderCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glProgramRef = useRef<WebGLProgram | null>(null);
  const glBgColorLocationRef = useRef<WebGLUniformLocation | null>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const [backgroundColor, setBackgroundColor] = useState([1.0, 1.0, 1.0]);

  useEffect(() => {
    const root = document.documentElement;
    const updateColor = () => {
      const isDark = root.classList.contains('dark');
      setBackgroundColor(isDark ? [0, 0, 0] : [1.0, 1.0, 1.0]);
    };
    updateColor();
    const observer = new MutationObserver(() => updateColor());
    observer.observe(root, { attributes: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const gl = glRef.current;
    const program = glProgramRef.current;
    const location = glBgColorLocationRef.current;
    if (gl && program && location) {
      gl.useProgram(program);
      gl.uniform3fv(location, new Float32Array(backgroundColor));
    }
  }, [backgroundColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl');
    if (!gl) return;
    glRef.current = gl;

    const vertexShaderSource = `attribute vec2 aPosition; void main() { gl_Position = vec4(aPosition, 0.0, 1.0); }`;
    const fragmentShaderSource = `
      precision highp float;
      uniform float iTime;
      uniform vec2 iResolution;
      uniform vec3 uBackgroundColor;
      mat2 rotate2d(float angle){ float c=cos(angle),s=sin(angle); return mat2(c,-s,s,c); }
      float variation(vec2 v1,vec2 v2,float strength,float speed){ return sin(dot(normalize(v1),normalize(v2))*strength+iTime*speed)/100.0; }
      vec3 paintCircle(vec2 uv,vec2 center,float rad,float width){
        vec2 diff = center-uv;
        float len = length(diff);
        len += variation(diff,vec2(0.,1.),5.,2.);
        len -= variation(diff,vec2(1.,0.),5.,2.);
        float circle = smoothstep(rad-width,rad,len)-smoothstep(rad,rad+width,len);
        return vec3(circle);
      }
      void main(){
        vec2 uv = gl_FragCoord.xy/iResolution.xy;
        uv.x *= 1.5; uv.x -= 0.25;
        float mask = 0.0;
        float radius = .35;
        vec2 center = vec2(.5);
        mask += paintCircle(uv,center,radius,.035).r;
        mask += paintCircle(uv,center,radius-.018,.01).r;
        mask += paintCircle(uv,center,radius+.018,.005).r;
        vec2 v=rotate2d(iTime)*uv;
        vec3 foregroundColor=vec3(v.x,v.y,.7-v.y*v.x);
        vec3 color=mix(uBackgroundColor,foregroundColor,mask);
        color=mix(color,vec3(1.),paintCircle(uv,center,radius,.003).r);
        gl_FragColor=vec4(color,1.);
      }`;

    const compileShader = (type: number, source: string) => {
      const shader = gl.createShader(type);
      if (!shader) throw new Error("Could not create shader");
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) || "Shader compilation error");
      }
      return shader;
    };

    const program = gl.createProgram();
    if (!program) throw new Error("Could not create program");
    gl.attachShader(program, compileShader(gl.VERTEX_SHADER, vertexShaderSource));
    gl.attachShader(program, compileShader(gl.FRAGMENT_SHADER, fragmentShaderSource));
    gl.linkProgram(program);
    gl.useProgram(program);
    glProgramRef.current = program;

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const aPosition = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

    const iTimeLoc = gl.getUniformLocation(program, 'iTime');
    const iResLoc = gl.getUniformLocation(program, 'iResolution');
    glBgColorLocationRef.current = gl.getUniformLocation(program, 'uBackgroundColor');
    gl.uniform3fv(glBgColorLocationRef.current, new Float32Array(backgroundColor));

    let animationFrameId: number;
    const render = (time: number) => {
      gl.uniform1f(iTimeLoc, time * 0.001);
      gl.uniform2f(iResLoc, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      animationFrameId = requestAnimationFrame(render);
    };
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    animationFrameId = requestAnimationFrame(render);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full -z-10" />;
};

// --- EXPORTED Types & Components --- //

export interface PricingCardProps {
  planName: string;
  badge?: string;
  description: React.ReactNode;
  price: string;
  priceLabel?: string;
  originalPrice?: string;
  discountBadge?: string;
  features: string[];
  buttonText: string;
  footerNote?: string;
  isPopular?: boolean;
  buttonVariant?: 'primary' | 'secondary';
  onClick?: () => void;
}

export const PricingCard = ({
  planName, badge, description, price, priceLabel, originalPrice, discountBadge, features, buttonText, footerNote, isPopular = false, buttonVariant = 'primary', onClick
}: PricingCardProps) => {
  return (
    <div className={`
      backdrop-blur-[14px] bg-gradient-to-br rounded-2xl shadow-xl flex-1 max-w-xs px-7 py-8 flex flex-col transition-all duration-300
      from-black/5 to-black/0 border border-black/10
      dark:from-white/10 dark:to-white/5 dark:border-white/10 dark:backdrop-brightness-[0.91]
      ${isPopular ? 'scale-105 relative ring-2 ring-primary/30 shadow-2xl' : ''}
    `}>
      {isPopular && (
        <span className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-xs font-bold px-5 py-1.5 rounded-full shadow-lg shadow-primary/30 tracking-wide">
          ⭐ Most members start here
        </span>
      )}
      <div className="mb-4">
        <h3 className="text-lg font-bold text-foreground">{planName}</h3>
        {badge && (
          <span className="inline-block text-xs font-semibold text-primary mt-1">{badge}</span>
        )}
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {discountBadge && (
        <div className="mb-3 inline-block px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold">
          {discountBadge}
        </div>
      )}
      <div className="flex items-baseline gap-1 mb-1">
        {originalPrice && (
          <span className="text-lg text-muted-foreground line-through mr-1">${originalPrice}</span>
        )}
        <span className="text-4xl font-extrabold text-foreground">${price}</span>
        <span className="text-muted-foreground text-sm">{priceLabel || '/mo'}</span>
      </div>
      <hr className="my-5 border-border" />
      <ul className="space-y-3 mb-8 flex-1">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2.5 text-sm text-foreground/80">
            <CheckIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /> {feature}
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        className={`
          mt-auto w-full py-3 rounded-xl font-semibold text-sm transition-all active:scale-[0.98]
          ${buttonVariant === 'primary'
            ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/25'
            : 'bg-muted hover:bg-muted/80 text-foreground border border-border'
          }
        `}
      >
        {buttonText}
      </button>
      {footerNote && (
        <p className="text-center text-xs text-muted-foreground mt-3 font-medium">{footerNote}</p>
      )}
    </div>
  );
};

interface ModernPricingPageProps {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  plans: PricingCardProps[];
  showAnimatedBackground?: boolean;
}

// Mobile compact card (collapsed state)
const MobilePricingPreview = ({
  plan,
  isSelected,
  onClick,
}: {
  plan: PricingCardProps;
  isSelected: boolean;
  onClick: () => void;
}) => (
  <motion.button
    onClick={onClick}
    layout
    className={`
      w-full text-left rounded-2xl px-5 py-4 transition-colors border backdrop-blur-[14px]
      ${isSelected
        ? 'bg-primary/10 border-primary/30 ring-2 ring-primary/20 shadow-lg'
        : 'bg-card/60 border-border hover:border-primary/20'
      }
    `}
  >
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-foreground">{plan.planName}</span>
          {plan.isPopular && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-primary to-primary/80 text-primary-foreground">
              ⭐ Most members start here
            </span>
          )}
        </div>
        {plan.discountBadge && (
          <span className="text-[10px] font-medium text-green-600 dark:text-green-400">{plan.discountBadge}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-baseline gap-0.5">
          {plan.originalPrice && (
            <span className="text-sm text-muted-foreground line-through mr-1">${plan.originalPrice}</span>
          )}
          <span className="text-2xl font-extrabold text-foreground">${plan.price}</span>
          <span className="text-xs text-muted-foreground">{plan.priceLabel || '/mo'}</span>
        </div>
        <motion.svg
          animate={{ rotate: isSelected ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="w-4 h-4 text-muted-foreground flex-shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </motion.svg>
      </div>
    </div>
  </motion.button>
);

export const ModernPricingPage = ({
  title,
  subtitle,
  plans,
  showAnimatedBackground = true,
}: ModernPricingPageProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(
    plans.findIndex((p) => p.isPopular) !== -1 ? plans.findIndex((p) => p.isPopular) : plans.length - 1
  );

  return (
    <div className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
      {showAnimatedBackground && <ShaderCanvas />}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 py-12 sm:py-20">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3">
            {title}
          </h2>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Desktop: side-by-side */}
        <div className="hidden sm:flex items-center sm:items-stretch justify-center gap-6">
          {plans.map((plan) => <PricingCard key={plan.planName} {...plan} />)}
        </div>

        {/* Mobile: stacked carousel */}
        <div className="flex flex-col gap-3 sm:hidden">
          {plans.map((plan, i) => (
            <div key={plan.planName}>
              <MobilePricingPreview
                plan={plan}
                isSelected={selectedIndex === i}
                onClick={() => setSelectedIndex(selectedIndex === i ? -1 : i)}
              />
              <AnimatePresence>
                {selectedIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="pt-3 pb-1 px-1">
                      <div className={`
                        backdrop-blur-[14px] bg-gradient-to-br rounded-2xl shadow-xl px-6 py-6 flex flex-col
                        from-black/5 to-black/0 border border-primary/20
                        dark:from-white/10 dark:to-white/5 dark:border-primary/20
                      `}>
                        <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                        <ul className="space-y-2.5 mb-6">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-foreground/80">
                              <CheckIcon className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" /> {feature}
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={plan.onClick}
                          className={`
                            w-full py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98]
                            ${plan.buttonVariant === 'primary'
                              ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/25'
                              : 'bg-muted hover:bg-muted/80 text-foreground border border-border'
                            }
                          `}
                        >
                          {plan.buttonText}
                        </button>
                        {plan.footerNote && (
                          <p className="text-center text-xs text-muted-foreground mt-3 font-medium">{plan.footerNote}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
